import React, { useEffect, useMemo, useState } from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import TextField from '@mui/material/TextField';
import AccessTime from '@mui/icons-material/AccessTime';
import AttachMoney from '@mui/icons-material/AttachMoney';
import Close from '@mui/icons-material/Close';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import PlaceOutlined from '@mui/icons-material/PlaceOutlined';
import SmsOutlined from '@mui/icons-material/SmsOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import { Link } from '@reach/router';
import moment from 'moment-timezone';
import { toast } from 'react-toastify';
import SearchableSelect from '../common/SearchableSelect';
import {
  EMAIL_TEMPLATES_PATH,
  SMS_TEMPLATES_PATH,
  getSelectedBoxId,
} from '../../utils/productLicensing';
import {
  fetchCampaignTemplates,
  previewCampaign,
  createCampaign,
  updateCampaign,
  runCampaignAction,
} from './api';

const COMMON_TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
  'America/Toronto',
  'America/Vancouver',
  'America/Mexico_City',
  'Europe/London',
  'Europe/Paris',
  'Asia/Kolkata',
  'Asia/Dubai',
  'Asia/Singapore',
  'Australia/Sydney',
];

const timezoneLabel = (zone) => {
  const name = zone || 'UTC';
  try {
    const offset = moment.tz(name).format('Z');
    return name === 'UTC' ? 'UTC' : `${name} (UTC${offset})`;
  } catch (_err) {
    return name;
  }
};

const nextScheduleSlot = (zone) => {
  const when = moment.tz(zone || 'UTC').add(1, 'hour').startOf('minute');
  const hour = when.hour();
  if (hour >= 20 || hour < 8) {
    const next = hour >= 20 ? when.clone().add(1, 'day') : when.clone();
    return next
      .hour(8)
      .minute(0)
      .second(0)
      .format('YYYY-MM-DDTHH:mm');
  }
  return when.format('YYYY-MM-DDTHH:mm');
};

const toScheduleInput = (value, zone) => {
  if (!value) return '';
  const parsed = moment(value);
  if (!parsed.isValid()) return '';
  return parsed.tz(zone || 'UTC').format('YYYY-MM-DDTHH:mm');
};

const FieldRow = ({ label, children }) => (
  <div className="campaign-form-row">
    <div className="campaign-form-label">{label}</div>
    <div className="campaign-form-control">{children}</div>
  </div>
);

const CampaignDialog = ({
  open,
  campaign,
  locations,
  t,
  onClose,
  onSaved,
}) => {
  const isEdit = !!(campaign && campaign.id);
  const [boxId, setBoxId] = useState('');
  const [channel, setChannel] = useState('email');
  const [audience, setAudience] = useState('one');
  const [audienceValue, setAudienceValue] = useState('');
  const [templateKey, setTemplateKey] = useState('');
  const [name, setName] = useState('');
  const [templates, setTemplates] = useState([]);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [actionBusy, setActionBusy] = useState('');
  const [sendMode, setSendMode] = useState('now');
  const [scheduleLocal, setScheduleLocal] = useState('');
  const [timeZone, setTimeZone] = useState('UTC');
  const [accountTimeZone, setAccountTimeZone] = useState('UTC');
  const [timezoneFromAccount, setTimezoneFromAccount] = useState(false);

  const selectedTemplate = useMemo(
    () => templates.find((item) => item.id === templateKey) || null,
    [templates, templateKey]
  );

  const locationItems = useMemo(
    () =>
      (locations || []).map((location) => ({
        id: location.id,
        name: location.name,
      })),
    [locations]
  );

  const templateItems = templates.map((item) => {
    const methodLabel = item.method_label || item.delivery_method;
    const eventName = item.event_label || item.event_key;
    const draftLabel = item.has_published
      ? ''
      : ` · ${t('Campaigns.StatusTemplateDraft', { defaultValue: 'Draft' })}`;
    const subject = item.email_subject ? ` — ${item.email_subject}` : '';
    return {
      id: item.id,
      name: `${methodLabel} · ${eventName}${draftLabel}${subject}`,
    };
  });
  const messageCenterPath =
    channel === 'email' ? EMAIL_TEMPLATES_PATH : SMS_TEMPLATES_PATH;

  const audiences = [
    {
      id: 'one',
      title: t('Campaigns.AudienceOne', { defaultValue: 'One patient' }),
      hint: t('Campaigns.AudienceOneHint', {
        defaultValue: 'Send to a single patient',
      }),
      Icon: PersonOutlined,
    },
    {
      id: 'all',
      title: t('Campaigns.AudienceAll', { defaultValue: 'All patients' }),
      hint: t('Campaigns.AudienceAllHint', {
        defaultValue: 'Send to all active patients',
      }),
      Icon: GroupsOutlined,
    },
    {
      id: 'zip',
              title: t('Campaigns.AudienceZipCard', { defaultValue: 'By zip code' }),
      hint: t('Campaigns.AudienceZipHint', {
        defaultValue: 'Send to patients in specific zip codes',
      }),
      Icon: PlaceOutlined,
    },
  ];

  const timezoneItems = useMemo(
    () => {
      const zones = COMMON_TIMEZONES.slice();
      if (timeZone && zones.indexOf(timeZone) === -1) zones.unshift(timeZone);
      if (accountTimeZone && zones.indexOf(accountTimeZone) === -1) {
        zones.unshift(accountTimeZone);
      }
      return zones.map((zone) => ({
        id: zone,
        name: timezoneLabel(zone),
      }));
    },
    [timeZone, accountTimeZone]
  );

  const loadTemplates = async (nextBoxId, nextChannel, preferred, resetZone) => {
    if (!nextBoxId) {
      setTemplates([]);
      setTemplateKey('');
      return;
    }
    setLoadingTemplates(true);
    try {
      const published = await fetchCampaignTemplates(nextBoxId, nextChannel);
      const list = (published && published.templates) || [];
      const zone = (published && published.timezone) || 'UTC';
      setTemplates(list);
      setAccountTimeZone(zone);
      setTimezoneFromAccount(!!(published && published.timezone_from_account));
      if (resetZone) {
        const nextZone =
          (preferred && preferred.timezone) || zone || 'UTC';
        setTimeZone(nextZone);
        if (preferred && preferred.scheduled_for) {
          setSendMode('schedule');
          setScheduleLocal(toScheduleInput(preferred.scheduled_for, nextZone));
        } else {
          setSendMode('now');
          setScheduleLocal(nextScheduleSlot(nextZone));
        }
      }
      if (preferred && preferred.event_key) {
        const match = list.find(
          (item) =>
            item.delivery_method === preferred.delivery_method &&
            item.event_key === preferred.event_key
        );
        setTemplateKey((match && match.id) || '');
      } else {
        setTemplateKey('');
      }
    } catch (err) {
      setError(err.message);
      setTemplates([]);
      setTemplateKey('');
      if (resetZone) {
        setTimeZone('UTC');
        setAccountTimeZone('UTC');
        setTimezoneFromAccount(false);
      }
    } finally {
      setLoadingTemplates(false);
    }
  };

  useEffect(
    () => {
      if (!open) return undefined;
      setError('');
      setPreview(null);
      setActionBusy('');
      const selectedBoxId = getSelectedBoxId();
      const nextBoxId =
        (campaign && campaign.box_id) ||
        ((locations || []).find((location) => location.id === selectedBoxId) &&
          selectedBoxId) ||
        (locations[0] && locations[0].id) ||
        '';
      const nextChannel = (campaign && campaign.channel) || 'email';
      setBoxId(nextBoxId);
      setChannel(nextChannel);
      setAudience((campaign && campaign.audience) || 'one');
      setAudienceValue((campaign && campaign.audience_value) || '');
      setName((campaign && campaign.name) || '');
      setTemplateKey('');
      loadTemplates(nextBoxId, nextChannel, campaign, true);
      return undefined;
    },
    [open, campaign]
  );

  const payload = () => {
    if (!selectedTemplate) return null;
    return {
      box_id: boxId,
      name: name || 'Campaign',
      channel,
      audience,
      audience_value: audienceValue,
      delivery_method: selectedTemplate.delivery_method,
      event_key: selectedTemplate.event_key,
      timezone: timeZone || 'UTC',
      scheduled_for: sendMode === 'schedule' ? scheduleLocal : null,
    };
  };

  const showError = (message) => {
    const text =
      message ||
      t('Campaigns.RequestFailed', { defaultValue: 'Request failed.' });
    setError(text);
    toast.error(text, { autoClose: 5000 });
  };

  const requireTemplate = (forSend) => {
    if (!boxId || !selectedTemplate) {
      showError(
        t('Campaigns.SendRequired', {
          defaultValue: 'Choose a location and a Studio template.',
        })
      );
      return false;
    }
    if (forSend && !selectedTemplate.has_published) {
      showError(
        t('Campaigns.PublishFirst', {
          defaultValue:
            'Publish this template in Studio before sending a campaign.',
        })
      );
      return false;
    }
    if (audience === 'one' && !String(audienceValue || '').trim()) {
      showError(
        t('Campaigns.PatientRequired', {
          defaultValue:
            'Enter a patient phone, email, username, or name.',
        })
      );
      return false;
    }
    if (audience === 'zip' && !String(audienceValue || '').trim()) {
      showError(
        t('Campaigns.ZipRequired', {
          defaultValue: 'Enter a zip code.',
        })
      );
      return false;
    }
    if (sendMode === 'schedule' && !scheduleLocal) {
      showError(
        t('Campaigns.ScheduleRequired', {
          defaultValue: 'Choose a date and time for this campaign.',
        })
      );
      return false;
    }
    return true;
  };

  const runPreview = async () => {
    if (!requireTemplate()) return;
    setActionBusy('preview');
    setError('');
    try {
      const data = await previewCampaign(payload());
      setPreview(data);
      if (!data.recipient_count) {
        showError(
          data.warning ||
            t('Campaigns.NoRecipients', {
              defaultValue: 'No recipients match that audience.',
            })
        );
      }
    } catch (err) {
      showError(err.message);
    } finally {
      setActionBusy('');
    }
  };

  const finish = (saved, toastKey, defaultValue) => {
    if (typeof onSaved === 'function') {
      onSaved(saved, toastKey, defaultValue);
    }
  };

  const saveDraft = async () => {
    if (!requireTemplate()) return;
    setActionBusy('save');
    setError('');
    try {
      const body = payload();
      if (isEdit) {
        const data = await updateCampaign(campaign.id, body);
        finish(
          data && data.campaign,
          'Campaigns.UpdatedToast',
          'Campaign updated.'
        );
      } else {
        const data = await createCampaign({ ...body, send: false });
        finish(
          data && data.campaign,
          'Campaigns.SavedToast',
          'Campaign saved as a draft.'
        );
      }
    } catch (err) {
      showError(err.message);
    } finally {
      setActionBusy('');
    }
  };

  const sendNow = async () => {
    if (!requireTemplate(true)) return;
    setActionBusy('send');
    setError('');
    try {
      const body = payload();
      let saved = campaign;
      if (isEdit) {
        const updated = await updateCampaign(campaign.id, body);
        saved = updated && updated.campaign;
        const sent = await runCampaignAction(campaign.id, 'send');
        saved = (sent && sent.campaign) || saved;
      } else {
        const created = await createCampaign({ ...body, send: true });
        saved = created && created.campaign;
      }
      const scheduled = saved && saved.status === 'scheduled';
      const failed =
        saved &&
        (saved.status === 'failed' ||
          saved.send_failed ||
          saved.outcome === 'failed');
      finish(
        saved,
        scheduled
          ? 'Campaigns.ScheduledToast'
          : failed
            ? 'Campaigns.FailedToast'
            : 'Campaigns.SentToast',
        scheduled
          ? 'Promotional messages cannot go out overnight. This campaign is scheduled for 8:00 AM.'
          : failed
            ? (saved && saved.error) ||
              'Campaign send failed. None of the messages were delivered.'
            : 'Campaign sent.'
      );
    } catch (err) {
      showError(err.message);
    } finally {
      setActionBusy('');
    }
  };

  const sendAllowed =
    !isEdit ||
    (campaign &&
      String(campaign.status || '').toLowerCase() !== 'cancelled' &&
      campaign.allowed_actions &&
      campaign.allowed_actions.send);
  const canSend =
    sendAllowed && selectedTemplate && selectedTemplate.has_published;
  const locked = !!actionBusy;

  const setAudienceChoice = (next) => {
    setAudience(next);
    setPreview(null);
    if (next === 'all') setAudienceValue('');
  };

  const setChannelChoice = (next) => {
    if (next === channel) return;
    setChannel(next);
    setPreview(null);
    loadTemplates(boxId, next, null, false);
  };

  return (
    <Dialog
      open={!!open}
      onClose={() => {
        if (!locked) onClose();
      }}
      className="mcc-confirm-dialog"
      maxWidth="md"
      PaperProps={{ className: 'mcc-confirm-paper mcc-campaign-dialog-paper' }}
    >
      {loadingTemplates || actionBusy ? (
        <LinearProgress className="mcc-dialog-progress" />
      ) : null}
      <DialogTitle className="mcc-confirm-title">
        {isEdit
          ? t('Campaigns.Edit', { defaultValue: 'Edit campaign' })
          : t('Campaigns.New', { defaultValue: 'New campaign' })}
        <IconButton
          aria-label={t('MessageControl.Close', { defaultValue: 'Close' })}
          onClick={onClose}
          disabled={locked}
          size="small"
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <p className="campaign-form-intro">
          {t('Campaigns.DialogIntro', {
            defaultValue:
              'Create and schedule promotional campaigns to engage patients via email or SMS.',
          })}
        </p>
        <div className="campaign-form">
          <FieldRow
            label={t('Campaigns.Name', { defaultValue: 'Campaign name' })}
          >
            <TextField
              placeholder={t('Campaigns.Name', { defaultValue: 'Campaign name' })}
              value={name}
              onChange={(event) => setName(event.target.value)}
              size="small"
              fullWidth
              variant="outlined"
            />
          </FieldRow>
          <FieldRow
            label={t('Campaigns.Location', { defaultValue: 'Location' })}
          >
            <SearchableSelect
              id="campaign-location"
              label={t('Campaigns.Location', { defaultValue: 'Location' })}
              items={locationItems}
              value={boxId}
              variant="outlined"
              showLabel={false}
              menuZIndex={1600}
              onChange={(event) => {
                const next = event.target.value;
                setBoxId(next);
                setPreview(null);
                loadTemplates(next, channel, null, true);
              }}
            />
          </FieldRow>
          <FieldRow
            label={t('Campaigns.Audience', { defaultValue: 'Audience' })}
          >
            <div className="campaign-audience-cards">
              {audiences.map((item) => {
                const selected = audience === item.id;
                const Icon = item.Icon;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={
                      'campaign-audience-card' +
                      (selected ? ' is-selected' : '')
                    }
                    aria-pressed={selected}
                    disabled={locked}
                    onClick={() => setAudienceChoice(item.id)}
                  >
                    <span className="campaign-audience-radio" />
                    <Icon className="campaign-audience-icon" fontSize="small" />
                    <span className="campaign-audience-copy">
                      <span className="campaign-audience-title">
                        {item.title}
                      </span>
                      <span className="campaign-audience-hint">
                        {item.hint}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="campaign-form-hint">
              {t('Campaigns.AudienceHint', {
                defaultValue: 'Select the audience for this campaign.',
              })}
            </p>
            {audience !== 'all' ? (
              <TextField
                label={
                  audience === 'zip'
                    ? t('Campaigns.Zip', { defaultValue: 'Zip code' })
                    : t('Campaigns.Patient', {
                        defaultValue:
                          'Patient phone, email, username, or name',
                      })
                }
                value={audienceValue}
                onChange={(event) => {
                  setAudienceValue(event.target.value);
                  setPreview(null);
                }}
                size="small"
                fullWidth
                variant="outlined"
                className="campaign-audience-input"
              />
            ) : null}
          </FieldRow>
          <FieldRow
            label={t('Campaigns.Channel', { defaultValue: 'Channel' })}
          >
            <div className="campaign-channel-toggle" role="group">
              <button
                type="button"
                className={
                  'campaign-channel-btn' +
                  (channel === 'email' ? ' is-selected' : '')
                }
                aria-pressed={channel === 'email'}
                disabled={locked}
                onClick={() => setChannelChoice('email')}
              >
                <EmailOutlined fontSize="small" />
                {t('Campaigns.Email', { defaultValue: 'Email' })}
              </button>
              <button
                type="button"
                className={
                  'campaign-channel-btn' +
                  (channel === 'sms' ? ' is-selected' : '')
                }
                aria-pressed={channel === 'sms'}
                disabled={locked}
                onClick={() => setChannelChoice('sms')}
              >
                <SmsOutlined fontSize="small" />
                {t('Campaigns.Sms', { defaultValue: 'SMS' })}
              </button>
            </div>
            <p className="campaign-form-hint">
              {t('Campaigns.ChannelHint', {
                defaultValue: 'Choose how the campaign will be delivered.',
              })}
            </p>
          </FieldRow>
          <FieldRow
            label={t('Campaigns.Template', { defaultValue: 'Template' })}
          >
            <div className="campaign-template-field">
              <DescriptionOutlined
                className="campaign-template-icon"
                fontSize="small"
              />
              <SearchableSelect
                id="campaign-template"
                label={t('Campaigns.Template', { defaultValue: 'Template' })}
                items={templateItems}
                value={templateKey}
                variant="outlined"
                showLabel={false}
                menuZIndex={1600}
                onChange={(event) => {
                  setTemplateKey(event.target.value);
                  setPreview(null);
                }}
              />
            </div>
            {loadingTemplates ? (
              <p className="campaign-form-hint">
                {t('Campaigns.BusyTemplates', {
                  defaultValue: 'Loading templates…',
                })}
              </p>
            ) : null}
            <p className="campaign-form-hint">
              {t('Campaigns.TemplateHint', {
                defaultValue:
                  'Choose from approved templates in Studio. Templates must be active and approved for promotional use.',
              })}
            </p>
            {!loadingTemplates && !templates.length ? (
              <p className="mcc-campaign-template-empty">
                {t('Campaigns.EmptyTemplates', {
                  defaultValue:
                    'No {{channel}} templates at this location yet. Create one in Studio, then come back here.',
                  channel: channel === 'email' ? 'email' : 'SMS',
                })}{' '}
                <Link to={messageCenterPath} onClick={onClose}>
                  {t('Campaigns.OpenMessageCenter', {
                    defaultValue: 'Open Studio',
                  })}
                </Link>
              </p>
            ) : null}
            {selectedTemplate && !selectedTemplate.has_published ? (
              <p className="mcc-campaign-draft-note">
                {t('Campaigns.DraftTemplate', {
                  defaultValue:
                    'This template is still a draft. Publish it in Studio before sending.',
                })}{' '}
                <Link to={messageCenterPath} onClick={onClose}>
                  {t('Campaigns.OpenMessageCenter', {
                    defaultValue: 'Open Studio',
                  })}
                </Link>
              </p>
            ) : null}
          </FieldRow>
          <FieldRow
            label={t('Campaigns.Schedule', { defaultValue: 'Schedule' })}
          >
            <div className="campaign-channel-toggle" role="group">
              <button
                type="button"
                className={
                  'campaign-channel-btn' +
                  (sendMode === 'now' ? ' is-selected' : '')
                }
                aria-pressed={sendMode === 'now'}
                disabled={locked}
                onClick={() => {
                  setSendMode('now');
                  setPreview(null);
                }}
              >
                {t('Campaigns.SendNow', { defaultValue: 'Send now' })}
              </button>
              <button
                type="button"
                className={
                  'campaign-channel-btn' +
                  (sendMode === 'schedule' ? ' is-selected' : '')
                }
                aria-pressed={sendMode === 'schedule'}
                disabled={locked}
                onClick={() => {
                  setSendMode('schedule');
                  setPreview(null);
                  if (!scheduleLocal) {
                    setScheduleLocal(nextScheduleSlot(timeZone));
                  }
                }}
              >
                {t('Campaigns.ScheduleLater', {
                  defaultValue: 'Schedule for later',
                })}
              </button>
            </div>
            <div className="campaign-schedule-fields">
              {sendMode === 'schedule' ? (
                <TextField
                  type="datetime-local"
                  label={t('Campaigns.SendAt', {
                    defaultValue: 'Send at',
                  })}
                  value={scheduleLocal}
                  onChange={(event) => {
                    setScheduleLocal(event.target.value);
                    setPreview(null);
                  }}
                  size="small"
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                />
              ) : null}
              <SearchableSelect
                id="campaign-timezone"
                label={t('Campaigns.TimeZone', { defaultValue: 'Time zone' })}
                items={timezoneItems}
                value={timeZone}
                variant="outlined"
                menuZIndex={1600}
                onChange={(event) => {
                  setTimeZone(event.target.value);
                  setPreview(null);
                }}
              />
            </div>
            <p className="campaign-form-hint">
              {timezoneFromAccount
                ? t('Campaigns.TimeZoneFromAccount', {
                    defaultValue:
                      'Time zone comes from account settings. You can change it for this campaign.',
                  })
                : t('Campaigns.TimeZoneUtcFallback', {
                    defaultValue:
                      'This account has no timezone set, so UTC is used. You can choose another timezone for this campaign.',
                  })}
            </p>
            <div className="campaign-schedule-box">
              <AccessTime fontSize="small" />
              <div>
                <strong>
                  {t('Campaigns.ScheduleTitle', {
                    defaultValue: 'Daytime send window only',
                  })}
                </strong>
                <p>
                  {t('Campaigns.ScheduleBodyTz', {
                    defaultValue:
                      'Campaigns will only send between 8:00 AM – 8:00 PM in {{timezone}}. Messages cannot be sent overnight (8:00 PM – 7:59 AM).',
                    timezone: timezoneLabel(timeZone),
                  })}
                </p>
              </div>
            </div>
          </FieldRow>
        </div>
        <p className="campaign-pricing">
          <AttachMoney fontSize="small" />
          <span>
            <strong>
              {t('Campaigns.PricingLabel', { defaultValue: 'Pricing:' })}
            </strong>{' '}
            {t('Campaigns.PricingHint', {
              defaultValue:
                'Billed per message or per thousand, depending on channel.',
            })}
          </span>
        </p>
        <div className="campaign-warning">
          <WarningAmberOutlined fontSize="small" />
          <span>
            <strong>
              {t('Campaigns.Important', { defaultValue: 'Important:' })}
            </strong>{' '}
            {t('Campaigns.SeparatePathWarning', {
              defaultValue:
                'Promotional traffic uses a separate send path, not transactional Ready SMS.',
            })}
          </span>
        </div>
        {error ? <p className="mcc-error">{error}</p> : null}
        {preview ? (
          <div className="mcc-campaigns-preview">
            <p>
              {t('Campaigns.Recipients', {
                defaultValue: '{{count}} recipients',
                count: preview.recipient_count,
              })}
              {' · '}
              {preview.price_label}
            </p>
            {preview.quiet_hours ? (
              <p className="mcc-campaigns-quiet">
                {t('Campaigns.QuietHours', {
                  defaultValue:
                    'Quiet hours (8:00 PM–8:00 AM). This will send at 8:00 AM.',
                })}
              </p>
            ) : null}
            {preview.preview_text ? <pre>{preview.preview_text}</pre> : null}
          </div>
        ) : null}
      </DialogContent>
      <DialogActions className="mcc-confirm-actions mcc-campaign-dialog-actions">
        <Button
          onClick={runPreview}
          disabled={locked}
          variant="outlined"
          sx={{ textTransform: 'none' }}
          startIcon={
            actionBusy === 'preview' ? (
              <CircularProgress size={16} color="inherit" />
            ) : null
          }
        >
          {actionBusy === 'preview'
            ? t('Campaigns.BusyPreview', {
                defaultValue: 'Counting recipients…',
              })
            : t('Campaigns.Preview', {
                defaultValue: 'Preview count and price',
              })}
        </Button>
        <span className="mcc-campaign-dialog-spacer" />
        <Button
          onClick={onClose}
          disabled={locked}
          variant="outlined"
          sx={{ textTransform: 'none' }}
        >
          {t('MessageControl.Cancel', { defaultValue: 'Cancel' })}
        </Button>
        <Button
          onClick={saveDraft}
          disabled={locked}
          variant="outlined"
          sx={{ textTransform: 'none' }}
          startIcon={
            actionBusy === 'save' ? (
              <CircularProgress size={16} color="inherit" />
            ) : null
          }
        >
          {actionBusy === 'save'
            ? t('Campaigns.BusySave', { defaultValue: 'Saving…' })
            : isEdit
              ? t('Campaigns.Save', { defaultValue: 'Save' })
              : t('Campaigns.SaveDraft', { defaultValue: 'Save draft' })}
        </Button>
        {sendAllowed ? (
          <Button
            onClick={sendNow}
            disabled={locked || !canSend}
            variant="contained"
            sx={{ textTransform: 'none', background: '#3b5bdb' }}
            startIcon={
              actionBusy === 'send' ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {actionBusy === 'send'
              ? sendMode === 'schedule'
                ? t('Campaigns.BusySchedule', {
                    defaultValue: 'Scheduling…',
                  })
                : t('Campaigns.BusySend', {
                    defaultValue: 'Sending campaign…',
                  })
              : sendMode === 'schedule'
                ? t('Campaigns.ScheduleSend', {
                    defaultValue: 'Schedule campaign',
                  })
                : isEdit
                  ? t('Campaigns.Send', { defaultValue: 'Send campaign' })
                  : t('Campaigns.Create', {
                      defaultValue: 'Create campaign',
                    })}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

export default CampaignDialog;



// WEBPACK FOOTER //
// ./src/components/campaigns/CampaignDialog.js